// Enhanced diagnostic logging for React Error #130 debugging
declare global {
  interface Window {
    __ADS_SCRIPT_DETECTED__?: boolean;
    __REACT_ERROR_COUNT__?: number;
    __DIAGNOSTIC_LOGS__?: Array<{timestamp: string, event: string, data: any}>;
  }
}

// Initialize diagnostics
window.__REACT_ERROR_COUNT__ = 0;
window.__DIAGNOSTIC_LOGS__ = [];

export const diagnostics = {
  log: (event: string, data: any) => {
    const entry = {
      timestamp: new Date().toISOString(),
      event,
      data
    };
    
    window.__DIAGNOSTIC_LOGS__?.push(entry);
    console.log(`🔍 [DIAGNOSTIC] ${event}:`, data);
    
    // Keep only last 50 entries
    if (window.__DIAGNOSTIC_LOGS__ && window.__DIAGNOSTIC_LOGS__.length > 50) {
      window.__DIAGNOSTIC_LOGS__ = window.__DIAGNOSTIC_LOGS__.slice(-50);
    }
  },

  trackScriptLoading: () => {
    // Monitor all script additions
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as HTMLElement;
            
            if (element.tagName === 'SCRIPT') {
              const script = element as HTMLScriptElement;
              diagnostics.log('SCRIPT_ADDED', {
                src: script.src,
                hasContent: !!script.textContent,
                reactAvailable: !!window.React,
                documentState: document.readyState
              });
              
              if (script.src?.includes('ads') || script.src?.includes('analytics')) {
                window.__ADS_SCRIPT_DETECTED__ = true;
                diagnostics.log('ADS_SCRIPT_DETECTED', {
                  src: script.src,
                  timing: performance.now(),
                  documentState: document.readyState
                });
              }
            }
          }
        });
      });
    });
    
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true
    });
    
    diagnostics.log('SCRIPT_MONITORING_STARTED', {
      documentState: document.readyState,
      existingScripts: Array.from(document.querySelectorAll('script[src]')).length
    });
  },

  trackReactErrors: () => {
    // Enhanced error tracking
    const originalConsoleError = console.error;
    console.error = function(...args: any[]) {
      const message = args[0]?.toString() || '';
      
      if (message.includes('React error') || message.includes('Minified React error')) {
        window.__REACT_ERROR_COUNT__ = (window.__REACT_ERROR_COUNT__ || 0) + 1;
        
        diagnostics.log('REACT_ERROR_DETECTED', {
          errorNumber: window.__REACT_ERROR_COUNT__,
          message: message.substring(0, 200),
          hasAdsScript: !!window.__ADS_SCRIPT_DETECTED__,
          stack: args[1]?.stack || new Error().stack,
          allArgs: args.map(arg => typeof arg === 'string' ? arg.substring(0, 100) : typeof arg)
        });
      }
      
      return originalConsoleError.apply(console, args);
    };

    // Track unhandled errors
    window.addEventListener('error', (event) => {
      diagnostics.log('UNHANDLED_ERROR', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        hasAdsScript: !!window.__ADS_SCRIPT_DETECTED__,
        stack: event.error?.stack
      });
    });

    diagnostics.log('ERROR_TRACKING_STARTED', {
      reactAvailable: !!window.React,
      documentState: document.readyState
    });
  },

  trackComponentMounting: () => {
    // Track React component mounting issues
    const originalCreateElement = window.React?.createElement;
    if (originalCreateElement) {
      window.React.createElement = function(type: any, ...args: any[]) {
        if (type === undefined || type === null) {
          diagnostics.log('INVALID_COMPONENT_TYPE', {
            type,
            hasAdsScript: !!window.__ADS_SCRIPT_DETECTED__,
            stack: new Error().stack?.split('\n').slice(0, 10),
            args: args.length
          });
        }
        return originalCreateElement.apply(this, [type, ...args]);
      };
      
      diagnostics.log('REACT_CREATEELEMENT_HOOKED', {
        reactVersion: window.React?.version
      });
    }
  },

  getDiagnosticReport: () => {
    return {
      errorCount: window.__REACT_ERROR_COUNT__,
      adsScriptDetected: !!window.__ADS_SCRIPT_DETECTED__,
      logs: window.__DIAGNOSTIC_LOGS__?.slice(-20), // Last 20 entries
      currentScripts: Array.from(document.querySelectorAll('script[src]')).map(s => s.getAttribute('src')),
      documentState: document.readyState,
      reactAvailable: !!window.React,
      timestamp: new Date().toISOString()
    };
  },

  init: () => {
    diagnostics.log('DIAGNOSTICS_INIT', {
      userAgent: navigator.userAgent,
      documentState: document.readyState,
      reactAvailable: !!window.React,
      existingScripts: Array.from(document.querySelectorAll('script')).length
    });
    
    diagnostics.trackScriptLoading();
    diagnostics.trackReactErrors();
    
    // Wait for React to be available before hooking createElement
    if (window.React) {
      diagnostics.trackComponentMounting();
    } else {
      const checkReact = setInterval(() => {
        if (window.React) {
          clearInterval(checkReact);
          diagnostics.trackComponentMounting();
        }
      }, 100);
      
      // Stop checking after 5 seconds
      setTimeout(() => clearInterval(checkReact), 5000);
    }
  }
};

// Auto-initialize
if (typeof window !== 'undefined') {
  diagnostics.init();
}

// Global access for debugging
(window as any).__diagnostics__ = diagnostics;